import React, { useState, useEffect, useRef } from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import { ArrowLeft, Clock, User, Tag, Calendar, Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { BlogPost } from '@/types/blog'
import { getBlogPostById, getBlogPostMDXComponent } from '@/utils/blog'
import { TableOfContents } from '@/components/TableOfContents'

// 包装MDX组件，过滤掉frontmatter内容
const MDXContentWrapper: React.FC<{ Component: React.ComponentType }> = ({ Component }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!wrapperRef.current) return

    // 延迟执行，确保MDX内容已完全渲染
    const timeoutId = setTimeout(() => {
      if (!wrapperRef.current) return

      // 查找所有段落和代码块
      const allElements = wrapperRef.current.querySelectorAll('p, pre, code, div')
      
      allElements.forEach(element => {
        const text = element.textContent || ''
        
        // 检测frontmatter模式：
        // 1. 以 --- 开头，包含 title:, date:, author: 等
        // 2. 或者包含完整的frontmatter块（---...---）
        const hasFrontmatter = 
          text.match(/^---\s*\n/) || // 以 --- 开头
          (text.includes('title:') && text.includes('date:') && text.includes('author:')) ||
          text.match(/^(title|date|author|tags|excerpt):\s.*\n(title|date|author|tags|excerpt):\s/) // 包含多个frontmatter字段
        
        if (hasFrontmatter) {
          // 检查是否是完整的frontmatter块（以---开始和结束）
          const lines = text.split('\n')
          const firstLine = lines[0]?.trim()
          const lastLine = lines[lines.length - 1]?.trim()
          
          if (firstLine === '---' && (lastLine === '---' || lines.some(line => line.trim() === '---' && line !== firstLine))) {
            // 这是完整的frontmatter块，移除整个元素
            element.remove()
          } else if (text.match(/^(title|date|author|tags|excerpt):\s/)) {
            // 单独的frontmatter行，也移除
            element.remove()
          }
        }
      })

      // 也检查直接的文本节点（可能在段落中）
      const walker = document.createTreeWalker(
        wrapperRef.current,
        NodeFilter.SHOW_TEXT,
        null
      )

      const textNodesToRemove: Text[] = []
      let node: Node | null

      while ((node = walker.nextNode())) {
        const text = node.textContent || ''
        // 如果文本节点包含frontmatter模式
        if (text.match(/^(title|date|author|tags|excerpt):\s/) && 
            text.trim().split('\n').length <= 5) { // 只处理短文本（可能是frontmatter）
          textNodesToRemove.push(node as Text)
        }
      }

      // 移除找到的文本节点
      textNodesToRemove.forEach(textNode => {
        if (textNode.parentNode) {
          textNode.parentNode.removeChild(textNode)
        }
      })
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [])

  return (
    <div ref={wrapperRef}>
      <Component />
    </div>
  )
}

export const BlogDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [MDXComponent, setMDXComponent] = React.useState<React.ComponentType | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) {
        setError('文章ID不存在')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const data = await getBlogPostById(id)
        
        if (!data) {
          setError('文章未找到')
          setLoading(false)
          return
        }

        setPost(data)
        
        // 尝试获取MDX组件
        const mdxComponent = getBlogPostMDXComponent(id)
        if (mdxComponent) {
          setMDXComponent(() => mdxComponent)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载文章失败')
      } finally {
        setLoading(false)
      }
    }

    fetchPost()
  }, [id])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">加载中...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">文章未找到</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || '您要查找的文章不存在或已被移除。'}
          </p>
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <ArrowLeft size={16} />
            返回博客列表
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex gap-8">
        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="mb-8">
        <Link 
          to="/blog"
          className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6 text-sm font-medium"
        >
          <ArrowLeft size={16} />
          返回博客列表
        </Link>
        
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {post.title}
          </h1>
          
          {post.excerpt && (
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
              {post.excerpt}
            </p>
          )}
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 dark:text-gray-400 mb-4">
            {post.author && (
              <div className="flex items-center gap-1">
                <User size={14} />
                <span>{post.author}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Calendar size={14} />
              <span>{formatDate(post.date)}</span>
            </div>
            {post.updatedAt && post.updatedAt !== post.date && (
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>更新于 {formatDate(post.updatedAt)}</span>
              </div>
            )}
          </div>
          
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-md"
                >
                  <Tag size={10} />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

          {/* Blog Content */}
          <div 
            ref={contentRef}
            className="prose prose-lg dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-gray-900 dark:prose-code:text-gray-200 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300"
          >
            {MDXComponent ? (
              <MDXContentWrapper Component={MDXComponent} />
            ) : post.content && typeof post.content === 'string' ? (
              <ReactMarkdown>{post.content}</ReactMarkdown>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">内容加载中...</p>
            )}
          </div>
        </div>

        {/* Table of Contents Sidebar */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <TableOfContents contentRef={contentRef} />
        </aside>
      </div>
    </div>
  )
}
